import { MessageOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Drawer, Spin, Typography } from "antd";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { EnvVariables } from "@/env-variables";
import { isAssistantPanelEnabled } from "./assistant-endpoint";
import { AnswerView } from "./components/AnswerView";
import { Composer } from "./components/Composer";
import { RecentConversations } from "./components/RecentConversations";
import { useAssistantChat } from "./hooks/useAssistantChat";

interface DevAssistantPanelProps {
  callerUsername: string | undefined;
}

const DevAssistantPanel: React.FC<DevAssistantPanelProps> = ({ callerUsername }) => {
  const [open, setOpen] = useState(false);
  const { turns, sending, ask, rate, reset, recent, loadRecent, resume, loadError } =
    useAssistantChat(callerUsername);
  const bottom = useRef<HTMLDivElement>(null);

  const showingRecent = open && turns.length === 0;
  useEffect(() => {
    if (showingRecent && recent === undefined) void loadRecent();
  }, [showingRecent, recent, loadRecent]);

  const latestTurn = turns.at(-1);
  useEffect(() => {
    if (latestTurn !== undefined) bottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [latestTurn]);

  return (
    <>
      <Button type="primary" shape="round" icon={<MessageOutlined />} onClick={() => setOpen(true)}>
        Ask
      </Button>
      <Drawer
        title="Support Bot"
        placement="right"
        width={480}
        open={open}
        onClose={() => setOpen(false)}
        extra={
          <Button size="small" icon={<PlusOutlined />} onClick={reset} disabled={sending}>
            New
          </Button>
        }
        footer={<Composer sending={sending} onSend={(text, images) => void ask(text, images)} />}
      >
        {loadError && <Alert type="warning" showIcon message={loadError} className="mb-4" />}
        {turns.length === 0 && (
          <div className="flex flex-col gap-4">
            <Typography.Text type="secondary">
              Try: &quot;Why can&apos;t I record a payment on this invoice?&quot; or paste a
              screenshot of an error.
            </Typography.Text>
            <RecentConversations recent={recent} onResume={(id) => void resume(id)} />
          </div>
        )}
        <div className="flex flex-col gap-4">
          {turns.map((turn) => (
            <div key={turn.id} className="flex flex-col gap-2">
              <div className="self-end rounded-lg bg-blue-50 px-3 py-2">
                {turn.question}
                {turn.imageCount > 0 && (
                  <span className="ml-2 text-gray-500">📎 {turn.imageCount}</span>
                )}
              </div>
              <div className="rounded-lg border px-3 py-2">
                {turn.status === "pending" && (
                  <Spin size="small" tip="Searching code and tickets…">
                    <div className="h-8" />
                  </Spin>
                )}
                {turn.status === "failed" && <Alert type="error" showIcon message={turn.error} />}
                {turn.answer && (
                  <AnswerView
                    answer={turn.answer}
                    elapsedMs={turn.elapsedMs}
                    vote={turn.vote}
                    onRate={(vote) => void rate(turn, vote)}
                  />
                )}
                {turn.answer && turn.error && (
                  <Typography.Text type="danger">{turn.error}</Typography.Text>
                )}
              </div>
            </div>
          ))}
          <div ref={bottom} />
        </div>
      </Drawer>
    </>
  );
};

/** Asks as the signed-in user; remounting per user gives each sign-in its own Conversation. */
const SignedInAssistantPanel: React.FC = () => {
  const { user } = useAuth();
  return (
    <DevAssistantPanel
      key={user?.username ?? "local-default-caller"}
      callerUsername={user?.username}
    />
  );
};

/** "Ask" button for the header, opening the Support Bot panel; on the dev server and the dev deployment only. */
export const AssistantPanel: React.FC = () =>
  isAssistantPanelEnabled(EnvVariables.env) ? <SignedInAssistantPanel /> : null;
